import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Clock, ChevronRight, Shield, HelpCircle, FileText, 
  Mail, ArrowRight, User, Lock, AlertTriangle,
  MapPin, Info, Eye
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

/**
 * SCUTA HOME — "Where time becomes visible"
 * 
 * LOCKED COPY & VISUAL QA CHECKLIST:
 * - True black background (#000000)
 * - SCUTA logo visible
 * - Gold accent matches logo tone
 * - High-contrast text (no faint gray)
 * - Horizontal timelines only
 * - No social affordances (no comments, likes, reactions)
 */

// SCUTA Brand Colors (LOCKED - True Black)
const SCUTA_THEME = {
  bg: '#000000',            // True black background (LOCKED)
  surface: '#0A0A0A',        // Subtle surface
  surfaceHover: '#141414',   // Hover state
  text: '#E6E9EE',           // Archive White (high contrast)
  textSecondary: '#B0B0B0',  // Secondary (readable, not faint)
  textMuted: '#808080',      // Muted
  border: '#1A1A1A',         // Subtle borders
  accent: '#C2A45E',         // Oxidized Gold (matches logo)
  accentMuted: 'rgba(194, 164, 94, 0.12)'
};

// SCUTA Logo Component (LOCKED: Flat, single color, no shadows)
const ScutaLogo = ({ size = 'lg', emphasis = false }) => {
  const sizes = {
    sm: { icon: 20, text: 'text-lg', sub: 'text-xs' },
    md: { icon: 28, text: 'text-2xl', sub: 'text-xs' },
    lg: { icon: 40, text: 'text-4xl', sub: 'text-sm' },
    xl: { icon: 56, text: 'text-5xl', sub: 'text-base' }
  };
  const s = sizes[size] || sizes.lg;
  const logoColor = emphasis ? SCUTA_THEME.accent : SCUTA_THEME.text;
  
  return (
    <div className="flex flex-col items-center">
      <div 
        className="rounded-full flex items-center justify-center mb-4"
        style={{ 
          width: s.icon + 24, 
          height: s.icon + 24,
          border: `2px solid ${logoColor}`
        }}
      >
        <Clock style={{ width: s.icon, height: s.icon, color: logoColor }} strokeWidth={1.5} />
      </div>
      <h1 
        className={`font-light tracking-widest ${s.text}`} 
        style={{ color: logoColor, letterSpacing: '0.3em' }}
      >
        SCUTA
      </h1>
      <p className={`mt-2 ${s.sub}`} style={{ color: SCUTA_THEME.textSecondary }}>
        Where time becomes visible.
      </p>
    </div>
  );
};

export default function ScutaHome() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [timelines, setTimelines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('welcome');

  // Force true black background on body for SCUTA pages
  useEffect(() => {
    const originalBg = document.body.style.backgroundColor;
    document.body.style.backgroundColor = '#000000';
    return () => {
      document.body.style.backgroundColor = originalBg;
    };
  }, []);

  useEffect(() => {
    loadTimelines();
  }, []);

  const loadTimelines = async () => {
    try {
      const res = await axios.get(`${API}/api/scuta/timelines`);
      setTimelines(res.data.timelines || []);
    } catch (error) {
      console.error('Failed to load timelines:', error);
    } finally {
      setLoading(false);
    }
  };

  const isPersonalMember = user && ['personal_member', 'founder'].includes(user.membership_tier);

  // Navigation sections
  const sections = [
    { id: 'welcome', label: 'Welcome', icon: Eye },
    { id: 'what', label: 'What You Can Do', icon: Clock },
    { id: 'rules', label: 'Rules of Conduct', icon: Shield },
    { id: 'about', label: 'About SCUTA', icon: Info },
    { id: 'privacy', label: 'Privacy & Ownership', icon: FileText },
    { id: 'contact', label: 'Contact', icon: Mail }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: SCUTA_THEME.bg }}>
      {/* Header */}
      <header 
        className="sticky top-0 z-50 border-b"
        style={{ backgroundColor: SCUTA_THEME.bg, borderColor: SCUTA_THEME.border }}
      >
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3">
              <div 
                className="w-8 h-8 rounded-full flex items-center justify-center"
                style={{ border: `1.5px solid ${SCUTA_THEME.accent}` }}
              >
                <Clock className="w-4 h-4" style={{ color: SCUTA_THEME.accent }} />
              </div>
              <span className="font-light tracking-widest text-sm" style={{ color: SCUTA_THEME.text }}>SCUTA</span>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                onClick={() => navigate('/scuta/entries')}
                variant="ghost"
                className="font-medium text-sm"
                style={{ color: SCUTA_THEME.text }}
                data-testid="scuta-activity-records-btn"
              >
                Activity Records
              </Button>
              <Button 
                onClick={() => navigate('/scuta/explore')}
                className="font-medium text-sm"
                style={{ 
                  backgroundColor: SCUTA_THEME.accent, 
                  color: '#000000'
                }}
              >
                Explore Timelines
              </Button>
              {user && (
                <Button 
                  onClick={() => navigate('/')}
                  variant="ghost"
                  className="text-sm"
                  style={{ color: SCUTA_THEME.textMuted }}
                >
                  Back to LikeKind
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-12">
        <div className="flex gap-12">
          {/* Sidebar Navigation */}
          <nav className="w-48 flex-shrink-0">
            <div className="sticky top-20 space-y-1">
              {sections.map(section => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded text-left text-sm transition-colors"
                  style={{ 
                    backgroundColor: activeSection === section.id ? SCUTA_THEME.accentMuted : 'transparent',
                    color: activeSection === section.id ? SCUTA_THEME.accent : SCUTA_THEME.textSecondary
                  }}
                >
                  <section.icon className="w-4 h-4" />
                  {section.label}
                </button>
              ))}
            </div>
          </nav>

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            {/* WELCOME SECTION — SCUTA LOCKED COPY (DO NOT EDIT) */}
            {activeSection === 'welcome' && (
              <div className="space-y-12">
                {/* Logo */}
                <div className="text-center py-8">
                  <ScutaLogo size="xl" emphasis={true} />
                </div>

                {/* SCUTA Welcome Text — LOCKED COPY (DO NOT EDIT) */}
                {/* NO LIKEKIND LANGUAGE - NO SOCIAL TERMS - TIMELINE/OBSERVATION ONLY */}
                <div className="max-w-2xl mx-auto space-y-6" style={{ color: SCUTA_THEME.textSecondary }}>
                  <p className="leading-relaxed text-lg" style={{ color: SCUTA_THEME.text }}>
                    SCUTA is a timeline instrument.
                  </p>
                  
                  <p className="leading-relaxed">
                    It exists to help people see time clearly — not as opinion, not as noise, not as engagement — but as sequence, pattern, and consequence.
                  </p>
                  
                  <p className="leading-relaxed">
                    Here, timelines are horizontal.<br/>
                    Events are placed, not argued.<br/>
                    Sources are shown, not hidden.
                  </p>
                  
                  <p className="leading-relaxed">
                    You can explore history freely.<br/>
                    You can observe how events overlap, diverge, and repeat.
                  </p>
                  
                  <p className="leading-relaxed" style={{ color: SCUTA_THEME.text }}>
                    Nothing here is ranked or personalized.
                  </p>
                  
                  <p className="leading-relaxed">
                    If you choose to create your own timelines, they belong to you.<br/>
                    They are private by default and shared only by intention.
                  </p>
                  
                  <p className="leading-relaxed">
                    SCUTA does not tell you what to think.<br/>
                    It helps you see when things happened — so understanding can follow.
                  </p>
                </div>

                {/* PRIMARY CTA - Prominent and obvious */}
                <div className="text-center pt-12">
                  <Button 
                    onClick={() => navigate('/scuta/explore')}
                    className="font-medium text-lg px-8 py-6"
                    style={{ 
                      backgroundColor: SCUTA_THEME.accent,
                      color: '#000000',
                      border: 'none'
                    }}
                    size="lg"
                    data-testid="scuta-explore-cta"
                  >
                    <Clock className="w-5 h-5 mr-2" />
                    Explore Timelines
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* WHAT YOU CAN DO SECTION */}
            {activeSection === 'what' && (
              <div className="space-y-8">
                <h2 className="text-xl font-light tracking-wide" style={{ color: SCUTA_THEME.text }}>
                  WHAT YOU CAN DO HERE
                </h2>
                
                <div className="space-y-4" style={{ color: SCUTA_THEME.textSecondary }}>
                  <ul className="space-y-3 list-none">
                    <li className="flex items-start gap-2">
                      <span style={{ color: SCUTA_THEME.accent }}>•</span>
                      Explore curated historical timelines
                    </li>
                    <li className="flex items-start gap-2">
                      <span style={{ color: SCUTA_THEME.accent }}>•</span>
                      View parallel events across cultures and disciplines
                    </li>
                    <li className="flex items-start gap-2">
                      <span style={{ color: SCUTA_THEME.accent }}>•</span>
                      Adjust density to reveal emergence or detail
                    </li>
                    <li className="flex items-start gap-2">
                      <span style={{ color: SCUTA_THEME.accent }}>•</span>
                      Compare timelines side by side
                    </li>
                    <li className="flex items-start gap-2">
                      <span style={{ color: SCUTA_THEME.accent }}>•</span>
                      Observe patterns without interpretation layered on top
                    </li>
                  </ul>
                </div>

                <div className="pt-6 border-t" style={{ borderColor: SCUTA_THEME.border }}>
                  <p className="mb-4" style={{ color: SCUTA_THEME.text }}>
                    If you are connected to LikeKind, you may also:
                  </p>
                  <ul className="space-y-3 list-none" style={{ color: SCUTA_THEME.textSecondary }}>
                    <li className="flex items-start gap-2">
                      <span style={{ color: SCUTA_THEME.accent }}>•</span>
                      Create and manage personal timelines
                    </li>
                    <li className="flex items-start gap-2">
                      <span style={{ color: SCUTA_THEME.accent }}>•</span>
                      Share timelines intentionally, person-to-person
                    </li>
                    <li className="flex items-start gap-2">
                      <span style={{ color: SCUTA_THEME.accent }}>•</span>
                      Use timelines as part of groups or stewarded Commons
                    </li>
                  </ul>
                </div>

                <p className="pt-6 italic" style={{ color: SCUTA_THEME.textMuted }}>
                  SCUTA remains read-only unless you choose otherwise.
                </p>

                {/* Available Timelines */}
                <div className="pt-8">
                  <h3 className="text-lg font-light mb-4" style={{ color: SCUTA_THEME.text }}>
                    Available Timelines
                  </h3>
                  <div className="grid gap-2">
                    {/* Personal Timeline */}
                    <button
                      onClick={() => {
                        if (isPersonalMember) {
                          navigate('/scuta/explore?timeline=personal');
                        } else {
                          toast.info('Personal Timeline requires LikeKind membership');
                        }
                      }}
                      className="flex items-center gap-3 p-3 rounded text-left transition-colors"
                      style={{ 
                        backgroundColor: SCUTA_THEME.surfaceHover,
                        opacity: isPersonalMember ? 1 : 0.6
                      }}
                    >
                      {isPersonalMember ? (
                        <User className="w-5 h-5" style={{ color: SCUTA_THEME.accent }} />
                      ) : (
                        <Lock className="w-5 h-5" style={{ color: SCUTA_THEME.textMuted }} />
                      )}
                      <div className="flex-1">
                        <p className="text-sm" style={{ color: SCUTA_THEME.text }}>Personal Timeline</p>
                        <p className="text-xs" style={{ color: SCUTA_THEME.textMuted }}>
                          {isPersonalMember ? 'Your private history' : 'Requires LikeKind membership'}
                        </p>
                      </div>
                    </button>

                    {/* Local History */}
                    <button
                      onClick={() => navigate('/scuta/explore?timeline=local')}
                      className="flex items-center gap-3 p-3 rounded text-left transition-colors"
                      style={{ backgroundColor: SCUTA_THEME.surfaceHover }}
                    >
                      <MapPin className="w-5 h-5" style={{ color: SCUTA_THEME.accent }} />
                      <div className="flex-1">
                        <p className="text-sm" style={{ color: SCUTA_THEME.text }}>Local History</p>
                        <p className="text-xs" style={{ color: SCUTA_THEME.textMuted }}>Based on your location</p>
                      </div>
                    </button>

                    {/* Public Timelines */}
                    {timelines.map(timeline => (
                      <button
                        key={timeline.id}
                        onClick={() => navigate(`/scuta/explore?timeline=${timeline.id}`)}
                        className="flex items-center gap-3 p-3 rounded text-left transition-colors"
                        style={{ backgroundColor: SCUTA_THEME.surfaceHover }}
                      >
                        {timeline.requires_disclaimer ? (
                          <AlertTriangle className="w-5 h-5" style={{ color: SCUTA_THEME.accent }} />
                        ) : (
                          <Clock className="w-5 h-5" style={{ color: SCUTA_THEME.accent }} />
                        )}
                        <div className="flex-1">
                          <p className="text-sm" style={{ color: SCUTA_THEME.text }}>{timeline.name}</p>
                          <p className="text-xs" style={{ color: SCUTA_THEME.textMuted }}>{timeline.entry_count} entries</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* RULES OF CONDUCT SECTION */}
            {activeSection === 'rules' && (
              <div className="space-y-8">
                <h2 className="text-xl font-light tracking-wide" style={{ color: SCUTA_THEME.text }}>
                  RULES OF CONDUCT
                </h2>
                
                <div className="space-y-6" style={{ color: SCUTA_THEME.textSecondary }}>
                  <p className="font-medium" style={{ color: SCUTA_THEME.text }}>
                    SCUTA is not a debate space.
                  </p>
                  
                  <p>
                    There are no comments, no reactions, and no engagement signals here by design.
                  </p>
                  
                  <p>By using SCUTA, you agree to:</p>
                  
                  <ul className="space-y-3 list-none pl-4">
                    <li className="flex items-start gap-2">
                      <span style={{ color: SCUTA_THEME.accent }}>•</span>
                      Treat timelines as representations, not verdicts
                    </li>
                    <li className="flex items-start gap-2">
                      <span style={{ color: SCUTA_THEME.accent }}>•</span>
                      Understand that inclusion does not equal endorsement
                    </li>
                    <li className="flex items-start gap-2">
                      <span style={{ color: SCUTA_THEME.accent }}>•</span>
                      Respect that some timelines contain unresolved or difficult history
                    </li>
                    <li className="flex items-start gap-2">
                      <span style={{ color: SCUTA_THEME.accent }}>•</span>
                      Use the tool for learning, reflection, and contextual understanding
                    </li>
                  </ul>
                  
                  <p className="pt-4">
                    SCUTA does not mediate disputes about interpretation.<br />
                    It provides structure — not authority.
                  </p>
                </div>

                {/* Short Form Rules */}
                <div className="mt-12 p-6 rounded" style={{ backgroundColor: SCUTA_THEME.surface, border: `1px solid ${SCUTA_THEME.border}` }}>
                  <h3 className="text-sm font-medium tracking-wide mb-4" style={{ color: SCUTA_THEME.accent }}>
                    RULES OF USE (SHORT FORM)
                  </h3>
                  <ul className="space-y-2 text-sm" style={{ color: SCUTA_THEME.textSecondary }}>
                    <li>Observe first</li>
                    <li>Compare carefully</li>
                    <li>Do not assume intent</li>
                    <li>Do not treat sequence as judgment</li>
                    <li>Context matters</li>
                    <li>Silence is part of the design</li>
                  </ul>
                </div>
              </div>
            )}

            {/* ABOUT SECTION */}
            {activeSection === 'about' && (
              <div className="space-y-8">
                <h2 className="text-xl font-light tracking-wide" style={{ color: SCUTA_THEME.text }}>
                  ABOUT SCUTA
                </h2>
                
                <div className="space-y-6" style={{ color: SCUTA_THEME.textSecondary }}>
                  <p>
                    SCUTA is part of the LikeKind ecosystem, but it is intentionally separate.
                  </p>
                  
                  <div className="grid grid-cols-2 gap-4 py-4">
                    <div className="p-4 rounded" style={{ backgroundColor: SCUTA_THEME.surface }}>
                      <p className="font-medium mb-2" style={{ color: SCUTA_THEME.accent }}>SCUTA</p>
                      <p className="text-sm">handles time</p>
                    </div>
                    <div className="p-4 rounded" style={{ backgroundColor: SCUTA_THEME.surface }}>
                      <p className="font-medium mb-2" style={{ color: SCUTA_THEME.text }}>LikeKind</p>
                      <p className="text-sm">handles people</p>
                    </div>
                  </div>
                  
                  <p>
                    They connect where appropriate, and remain independent where necessary.
                  </p>
                  
                  <p>
                    SCUTA exists so that history, memory, and sequence are not flattened into feeds.
                  </p>
                </div>
              </div>
            )}

            {/* PRIVACY & OWNERSHIP SECTION */}
            {activeSection === 'privacy' && (
              <div className="space-y-8">
                <h2 className="text-xl font-light tracking-wide" style={{ color: SCUTA_THEME.text }}>
                  PRIVACY & OWNERSHIP
                </h2>
                
                <div className="space-y-6" style={{ color: SCUTA_THEME.textSecondary }}>
                  <ul className="space-y-3 list-none">
                    <li className="flex items-start gap-2">
                      <span style={{ color: SCUTA_THEME.accent }}>•</span>
                      Public timelines are viewable by anyone
                    </li>
                    <li className="flex items-start gap-2">
                      <span style={{ color: SCUTA_THEME.accent }}>•</span>
                      Personal timelines are owned by their creator
                    </li>
                    <li className="flex items-start gap-2">
                      <span style={{ color: SCUTA_THEME.accent }}>•</span>
                      Nothing is monetized, ranked, or sold based on behavior
                    </li>
                  </ul>
                  
                  <p className="pt-4">
                    If you create a timeline, you retain full ownership and export rights.
                  </p>
                  
                  <div className="pt-4">
                    <p className="mb-3">Permanence is ensured through:</p>
                    <ul className="space-y-2 list-none pl-4">
                      <li className="flex items-start gap-2">
                        <span style={{ color: SCUTA_THEME.accent }}>•</span>
                        Export to personal storage
                      </li>
                      <li className="flex items-start gap-2">
                        <span style={{ color: SCUTA_THEME.accent }}>•</span>
                        Optional archival formats (including print)
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* CONTACT SECTION */}
            {activeSection === 'contact' && (
              <div className="space-y-8">
                <h2 className="text-xl font-light tracking-wide" style={{ color: SCUTA_THEME.text }}>
                  CONTACT
                </h2>
                
                <div className="space-y-6" style={{ color: SCUTA_THEME.textSecondary }}>
                  <p>
                    Questions about SCUTA, stewardship, or archival use
                    can be directed through LikeKind support.
                  </p>
                  
                  <div className="p-4 rounded" style={{ backgroundColor: SCUTA_THEME.surface }}>
                    <a 
                      href="mailto:scuta@likekind.app" 
                      className="text-sm"
                      style={{ color: SCUTA_THEME.accent }}
                    >
                      scuta@likekind.app
                    </a>
                  </div>
                  
                  <p className="pt-4 italic" style={{ color: SCUTA_THEME.textMuted }}>
                    SCUTA does not accept advertising, sponsorship, or promotion requests.
                  </p>
                </div>
              </div>
            )}
          </main>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t py-8 mt-12" style={{ borderColor: SCUTA_THEME.border }}>
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div 
                className="w-6 h-6 rounded-full flex items-center justify-center"
                style={{ border: `1px solid ${SCUTA_THEME.textMuted}` }}
              >
                <Clock className="w-3 h-3" style={{ color: SCUTA_THEME.textMuted }} />
              </div>
              <span className="text-sm font-light tracking-wide" style={{ color: SCUTA_THEME.textMuted }}>SCUTA</span>
              <span className="text-xs" style={{ color: SCUTA_THEME.textMuted }}>Connected to LikeKind</span>
            </div>
            <p className="text-xs" style={{ color: SCUTA_THEME.textMuted }}>
              © 2026
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
